import React, { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PortalAuthProvider, usePortalAuth } from "./context/PortalAuthContext";

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-slate-50">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
            <div className="text-red-500 text-4xl mb-4">!</div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">Algo salio mal</h2>
            <p className="text-sm text-slate-500 mb-4">{this.state.error?.message || "Error desconocido"}</p>
            <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700">
              Recargar pagina
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Ventas from "./pages/Ventas";
import Clientes from "./pages/Clientes";
import Proveedores from "./pages/Proveedores";
import Productos from "./pages/Productos";
import Fabricacion from "./pages/Fabricacion";
import Planeacion from "./pages/Planeacion";
import Compras from "./pages/Compras";
import Inventario from "./pages/Inventario";
import ListasPrecios from "./pages/ListasPrecios";
import Reportes from "./pages/Reportes";
import Configuracion from "./pages/Configuracion";
import Comprobantes from "./pages/Comprobantes";
import Envios from "./pages/Envios";
import ImportarVentas from "./pages/ImportarVentas";
import api from "./utils/api";
import Surtido from "./pages/Surtido";
import Rentabilidad from "./pages/Rentabilidad";
import Recepciones from "./pages/Recepciones";
import Transformaciones from "./pages/Transformaciones";
import Gastos from "./pages/Gastos";
import Limpieza from "./pages/Limpieza";
import Trazabilidad from "./pages/Trazabilidad";
import Guia from "./pages/Guia";
import AiChat from "./components/AiChat";
// Portal pages
import PortalLogin from "./pages/portal/PortalLogin";
import PortalTienda from "./pages/portal/PortalTienda";
import PortalOrderDetail from "./pages/portal/PortalOrderDetail";

function ProtectedRoute({ children, module }) {
  const { user, hasPermission } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (module && !hasPermission(module)) return <Layout><div className="flex items-center justify-center h-64"><div className="text-center"><div className="text-4xl text-slate-300 mb-3">🔒</div><h2 className="text-lg font-bold text-slate-700">Sin acceso</h2><p className="text-sm text-slate-500 mt-1">No tienes permisos para ver esta seccion. Contacta al administrador.</p></div></div></Layout>;
  return <Layout>{children}</Layout>;
}

function PortalProtectedRoute({ children }) {
  const { customer, loading } = usePortalAuth();
  if (loading) return null;
  if (!customer) return <Navigate to="/tienda/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  useEffect(() => {
    api.post("/init").catch(() => {});
  }, []);

  return (
    <Routes>
      {/* ERP routes */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/" element={<ProtectedRoute module="dashboard"><Dashboard /></ProtectedRoute>} />
      <Route path="/ventas" element={<ProtectedRoute module="ventas"><Ventas /></ProtectedRoute>} />
      <Route path="/surtido" element={<ProtectedRoute module="surtido"><Surtido /></ProtectedRoute>} />
      <Route path="/compras" element={<ProtectedRoute module="compras"><Compras /></ProtectedRoute>} />
      <Route path="/clientes" element={<ProtectedRoute module="clientes"><Clientes /></ProtectedRoute>} />
      <Route path="/proveedores" element={<ProtectedRoute module="proveedores"><Proveedores /></ProtectedRoute>} />
      <Route path="/productos" element={<ProtectedRoute module="productos"><Productos /></ProtectedRoute>} />
      <Route path="/fabricacion" element={<ProtectedRoute module="fabricacion"><Fabricacion /></ProtectedRoute>} />
      <Route path="/planeacion" element={<ProtectedRoute module="planeacion"><Planeacion /></ProtectedRoute>} />
      <Route path="/inventario" element={<ProtectedRoute module="inventario"><Inventario /></ProtectedRoute>} />
      <Route path="/recepciones" element={<ProtectedRoute module="recepciones"><Recepciones /></ProtectedRoute>} />
      <Route path="/transformaciones" element={<ProtectedRoute module="transformaciones"><Transformaciones /></ProtectedRoute>} />
      <Route path="/gastos" element={<ProtectedRoute module="gastos"><Gastos /></ProtectedRoute>} />
      <Route path="/limpieza" element={<ProtectedRoute module="limpieza"><Limpieza /></ProtectedRoute>} />
      <Route path="/trazabilidad" element={<ProtectedRoute module="trazabilidad"><Trazabilidad /></ProtectedRoute>} />
      <Route path="/guia" element={<ProtectedRoute><Guia /></ProtectedRoute>} />
      <Route path="/listas-precios" element={<ProtectedRoute module="listas_precios"><ListasPrecios /></ProtectedRoute>} />
      <Route path="/reportes" element={<ProtectedRoute module="reportes"><Reportes /></ProtectedRoute>} />
      <Route path="/configuracion" element={<ProtectedRoute module="configuracion"><Configuracion /></ProtectedRoute>} />
      <Route path="/comprobantes" element={<ProtectedRoute module="comprobantes"><Comprobantes /></ProtectedRoute>} />
      <Route path="/envios" element={<ProtectedRoute module="envios"><Envios /></ProtectedRoute>} />
      <Route path="/importar-ventas" element={<ProtectedRoute module="importar_ventas"><ImportarVentas /></ProtectedRoute>} />
      <Route path="/rentabilidad" element={<ProtectedRoute module="rentabilidad"><Rentabilidad /></ProtectedRoute>} />
      {/* Portal routes */}
      <Route path="/tienda/login" element={<PortalLogin />} />
      <Route path="/tienda" element={<PortalProtectedRoute><PortalTienda /></PortalProtectedRoute>} />
      <Route path="/tienda/pedido/:orderId" element={<PortalProtectedRoute><PortalOrderDetail /></PortalProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <PortalAuthProvider>
            <AppRoutes />
            <AiChatWrapper />
            <Toaster richColors position="top-right" />
          </PortalAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

function AiChatWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return <AiChat />;
}

export default App;
